import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, BookOpen, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { setPageMeta, injectJsonLd, removeJsonLd } from "@/lib/seo";
import { homeSchema } from "@/lib/schema";

const QUICK_CITIES = ["Milano", "Roma", "Napoli", "Torino", "Bologna", "Firenze", "Venezia", "Palermo"];

export default function Home() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [stats, setStats] = useState({ schools: 0 });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/schools", { params: { limit: 1 } }).then(({ data }) => setStats({ schools: data.total })).catch(() => {});
    api.get("/products/categories").then(({ data }) => setCategories(data || [])).catch(() => {});
  }, []);

  // SEO meta + JSON-LD Home (WebSite + SearchAction + Organization)
  useEffect(() => {
    setPageMeta({
      title: "Per manina — Scuole dell'infanzia, libri e giochi educativi raccontati dai genitori",
      description: "La community indipendente dei genitori italiani: oltre 21.000 scuole dell'infanzia raccontate e una selezione curata di libri, giochi e strumenti educativi 0-8 anni.",
      canonicalPath: "/",
      ogType: "website",
    });
    injectJsonLd("schema-home", homeSchema());
    return () => removeJsonLd("schema-home");
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    navigate(`/cerca?${params.toString()}`);
  };

  const schoolsTotal = stats.schools > 0 ? stats.schools.toLocaleString("it-IT") : "21.000";

  return (
    <div className="bg-[#FAF9F6]">
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src="/ai/hero.jpg" alt="Aula di scuola dell'infanzia" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32">
          <div className="max-w-4xl">
            <h1 className="font-display font-semibold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight fade-up">
              Fatti accompagnare<br />dal cuore degli altri genitori<br />verso una scelta più consapevole.
            </h1>

            {/* DUE PILASTRI inline nell'hero */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl fade-up delay-2">
              {/* Scuole */}
              <div className="glass rounded-xl p-5 sm:p-6">
                <p className="font-ui text-sm sm:text-base text-[#1A362D] leading-relaxed">
                  Scopri, confronta e racconta la tua esperienza in oltre <strong>{schoolsTotal}</strong> scuole dell'infanzia italiane
                  tra <strong>pubbliche</strong>, <strong>private</strong> e <strong>paritarie</strong>.
                </p>
                <Link
                  to="/cerca"
                  data-testid="home-cta-schools"
                  className="mt-4 inline-flex items-center gap-2 btn-primary text-sm"
                >
                  <MapPin size={15} /> Cerca per città
                </Link>
              </div>

              {/* Libri & Giochi */}
              <div className="glass rounded-xl p-5 sm:p-6">
                <p className="font-ui text-sm sm:text-base text-[#1A362D] leading-relaxed">
                  Esplora per <strong>temi</strong>, commenta e fai conoscere libri o giochi che potrebbero servire alla community.
                </p>
                <Link
                  to="/prodotti"
                  data-testid="home-cta-products"
                  className="mt-4 inline-flex items-center gap-2 btn-primary text-sm"
                >
                  <BookOpen size={15} /> Scopri
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK SEARCH BAR + CITIES — sotto l'hero, secondario */}
      <section className="bg-white border-b border-[#E8E6E1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <form onSubmit={submitSearch} className="flex-1 flex items-center gap-2" data-testid="home-search-form">
            <div className="flex-1 flex items-center bg-[#FAF9F6] border border-[#E8E6E1] rounded-md px-3 py-2">
              <Search size={18} className="text-[#8A9691] mr-2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca scuola per nome, comune o CAP…"
                data-testid="search-input"
                className="flex-1 bg-transparent border-0 outline-none font-ui text-sm text-[#1A362D] placeholder:text-[#8A9691]"
              />
            </div>
            <button type="submit" data-testid="search-submit" className="btn-primary text-sm whitespace-nowrap">Cerca</button>
          </form>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-ui text-xs uppercase tracking-wider text-[#8A9691] mr-1">Città:</span>
            {QUICK_CITIES.map((c) => (
              <button
                key={c}
                onClick={() => navigate(`/migliori/${c.toLowerCase()}`)}
                data-testid={`city-filter-${c.toLowerCase()}`}
                className="px-2.5 py-1 rounded-full border border-[#E8E6E1] text-[#1A362D] font-ui text-xs hover:bg-[#1A362D] hover:text-white transition-colors"
              >
                {c}
              </button>
            ))}
            <Link
              to="/migliori"
              data-testid="explore-all-cities-link"
              className="px-2.5 py-1 rounded-full border border-[#C05C45] text-[#C05C45] font-ui text-xs hover:bg-[#C05C45] hover:text-white transition-colors"
            >
              Esplora tutte le città →
            </Link>
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES — sezione di scoperta */}
      {categories.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="label-eyebrow text-[#C05C45]">Libri & Giochi · Aree tematiche</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl text-[#1A362D] tracking-tight">
                Risorse per ogni sfida quotidiana
              </h2>
            </div>
            <Link to="/prodotti" data-testid="home-categories-all" className="font-ui text-sm font-medium text-[#C05C45] hover:text-[#A64A35] inline-flex items-center gap-1">
              Tutte le aree <ArrowRight size={14} />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.slice(0, 8).map((c) => (
              <Link
                key={c.slug}
                to={`/prodotti/${c.slug}`}
                data-testid={`home-category-${c.slug}`}
                className="group bg-white border border-[#E8E6E1] rounded-xl p-5 hover:-translate-y-1 hover:shadow-[0_8px_24px_-12px_rgba(26,54,45,0.18)] transition-all"
              >
                <div className="text-3xl mb-3" aria-hidden>{c.emoji}</div>
                <h3 className="font-display text-base text-[#1A362D] leading-tight group-hover:text-[#C05C45] transition-colors">{c.label}</h3>
                <p className="mt-1 font-ui text-[11px] text-[#8A9691]">{c.product_count} prodotti</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* TRUST STRIP */}
      <section className="bg-[#F2EFEB] border-y border-[#E8E6E1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Sparkles className="text-[#C05C45]" size={22} />
            <h3 className="mt-3 font-display text-xl text-[#1A362D]">Indipendente</h3>
            <p className="mt-1.5 font-ui text-sm text-[#5C6B64] leading-relaxed">
              Le esperienze sono dei genitori, non dei brand. Nessun pay-to-play sui ranking.
            </p>
          </div>
          <div>
            <ShieldCheck className="text-[#C05C45]" size={22} />
            <h3 className="mt-3 font-display text-xl text-[#1A362D]">GDPR-ready</h3>
            <p className="mt-1.5 font-ui text-sm text-[#5C6B64] leading-relaxed">
              Login solo con Google, nessuna password da ricordare. Cookie e pubblicità attivi solo con il tuo consenso, revocabile in ogni momento.
            </p>
          </div>
          <div>
            <BookOpen className="text-[#C05C45]" size={22} />
            <h3 className="mt-3 font-display text-xl text-[#1A362D]">Curata</h3>
            <p className="mt-1.5 font-ui text-sm text-[#5C6B64] leading-relaxed">
              Catalogo editoriale di libri e giochi verificato; la community aggiunge ciò che funziona davvero.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
